import React, { forwardRef, Fragment, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { Field, Form, Formik } from 'formik';
import moment from 'moment';
import { usePopperTooltip } from 'react-popper-tooltip';
import Select from 'react-select';
import * as Yup from 'yup';

import { useVenuesDailyDemand } from './context/venuesDailyDemandContext';

import { formatFigure } from 'Helpers/formatService';

import DateRangePickerField from 'CommonComponents/DateRangePickerField';
import IconButton from 'CommonComponents/IconButton';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';

/**
 * Enumerate all days from `start` to `end` (inclusive), formatted as `D-M-YYYY`
 * to match the existing date column key convention.
 */
const enumerateRangeDates = (start, end) => {
    if (!start || !end) {
        return [];
    }
    const dates = [];
    const cursor = start.clone();
    const stop = end.clone();
    while (cursor.isSameOrBefore(stop, 'day')) {
        dates.push(cursor.format('D-M-YYYY'));
        cursor.add(1, 'd');
    }
    return dates;
};

export default function VenuesDailyDemandTable() {
    const { venues, selectedVenue, setSelectedVenue, venueDailyDemands, range, setRange, stepRange, minDate, maxDate, loading } = useVenuesDailyDemand();

    const dates = useMemo(() => enumerateRangeDates(range.start, range.end), [range.start, range.end]);

    const [allExpanded, setAllExpanded] = useState(false);
    const [expandKey, setExpandKey] = useState(0);

    const handleToggleAll = () => {
        setAllExpanded((prev) => !prev);
        setExpandKey((k) => k + 1);
    };

    const showEmptyState = useMemo(() => {
        if (!selectedVenue || loading || !Array.isArray(venueDailyDemands)) {
            return false;
        }
        if (venueDailyDemands.length === 0) {
            return true;
        }
        // Only the Spectators row remains, and it has no ticketing data — treat
        // as empty so users see the "no allocation" hint instead of a sole zero row.
        if (venueDailyDemands.length === 1) {
            const only = venueDailyDemands[0];
            if (only.slug === 'spectators' && !only.number) {
                return true;
            }
        }
        return false;
    }, [selectedVenue, loading, venueDailyDemands]);

    return (
        <div className="table__card tw-space-y-4 tw-flex tw-flex-col tw-max-h-full tw-min-h-0">
            <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-y-3">
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <h1 className="tw-text-xl tw-font-semibold tw-m-0">{selectedVenue?.name || $t('Venue')}</h1>
                    <Select
                        className="react-select-container tw-w-52"
                        classNamePrefix="react-select"
                        name="venue"
                        aria-label={$t('Select venue')}
                        options={venues}
                        onChange={(value) => setSelectedVenue(value)}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.slug}
                        value={selectedVenue || undefined}
                    />
                </div>
                <DateRangeControl
                    start={range.start}
                    end={range.end}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={setRange}
                    onPrev={() => stepRange(-1)}
                    onNext={() => stepRange(1)}
                />
            </div>

            {showEmptyState ? (
                <EmptyState />
            ) : (
                <div
                    className="inbud-table_wrapper tw-overflow-auto tw-snap-x tw-snap-mandatory tw-min-h-0 tw-flex-auto"
                    style={{ scrollPaddingInlineStart: '21rem' }}
                >
                    <table className="inbud-table tw-table-fixed">
                        <thead className="tw-sticky tw-top-0 tw-z-20 tw-bg-white">
                            <tr>
                                <th rowSpan={2} className="tw-w-60 tw-sticky tw-left-0 tw-h-fit tw-bg-white tw-z-30">
                                    <div className="tw-flex tw-items-center tw-gap-x-2">
                                        <button
                                            type="button"
                                            onClick={handleToggleAll}
                                            className="tw-flex tw-items-center tw-gap-x-1 tw-text-xs tw-font-medium tw-text-charcoal-50 hover:tw-text-charcoal-75 tw-transition-colors tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
                                        >
                                            {allExpanded ? <ChevronUpIcon className="tw-w-3 tw-h-3" /> : <ChevronDownIcon className="tw-w-3 tw-h-3" />}
                                            <span>{allExpanded ? $t('Collapse all') : $t('Expand all')}</span>
                                        </button>
                                        <span className="tw-text-charcoal-50 tw-select-none">|</span>
                                        <span>{$t('Client Groups')}</span>
                                    </div>
                                </th>
                                <th rowSpan={2} className="tw-w-24 tw-sticky tw-left-60 tw-h-fit tw-bg-white tw-z-30">
                                    {$t('Number')}
                                </th>
                                {dates.map((date) => (
                                    <VenuesDailyDemandTableDateHeader key={date} date={date} />
                                ))}
                            </tr>
                            <tr className="*:!tw-pt-0.5">
                                {dates.map((date) => (
                                    <Fragment key={date}>
                                        <th className="tw-border-l tw-bg-white">{$t('Morning')}</th>
                                        <th className="tw-bg-white">{$t('Afternoon')}</th>
                                        <th className="tw-bg-white">{$t('Evening')}</th>
                                    </Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {selectedVenue && (
                                <VenuesDailyDemandMapper data={venueDailyDemands} dates={dates} forceExpanded={allExpanded} expandKey={expandKey} />
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function EmptyState() {
    return (
        <div className="tw-rounded-lg tw-border-2 tw-border-charcoal-50/30 tw-bg-white tw-py-8 tw-px-6 tw-text-center tw-text-charcoal-75">
            {$t('No sport allocated to this venue.')}
        </div>
    );
}

function VenuesDailyDemandTableDateHeader({ date }) {
    const [venueSports, setVenueSports] = useState([]);
    const [headerLoading, setHeaderLoading] = useState(false);

    const { fetchVenueSports, selectedVenue } = useVenuesDailyDemand();

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
        interactive: false,
        offset: [0, -2],
        delayHide: 100,
    });

    useEffect(() => {
        setVenueSports([]);
    }, [selectedVenue]);

    useEffect(() => {
        if (visible && selectedVenue && !venueSports.length) {
            setHeaderLoading(true);
            fetchVenueSports(selectedVenue.slug, moment(date, 'D-M-YYYY').format('YYYY-MM-DD'))
                .then(({ data }) => {
                    setVenueSports(data);
                })
                .finally(() => {
                    setHeaderLoading(false);
                });
        }
    }, [visible]);

    return (
        <th colSpan={3} className="tw-w-80 !tw-pb-0.5 tw-border-l tw-cursor-default tw-bg-white tw-snap-start" key={date} ref={setTriggerRef}>
            {visible && (
                <DateTooltip ref={setTooltipRef} sports={venueSports} loading={headerLoading} getTooltipProps={getTooltipProps} getArrowProps={getArrowProps} />
            )}
            {date}
        </th>
    );
}

const DateTooltip = forwardRef(function DateTooltip({ sports, loading, getTooltipProps }, ref) {
    return (
        <div
            ref={ref}
            data-popper-reference-hidden="true"
            {...getTooltipProps({
                className:
                    'tw-flex tw-flex-col tw-gap-y-2 tw-bg-white tw-p-3 tw-rounded-lg tw-border-2 tw-border-charcoal-50 tw-opacity-90 tw-w-full tw-z-[999]',
                style: { maxWidth: '500px' },
            })}
        >
            <div className="tw-overflow-auto tw-max-h-full tw-p-0 tw-bg-transparent">
                {loading && !sports?.length && <LoadingSpinner />}
                {!loading && !sports?.length && <div>{$t('Something went wrong')}</div>}
                {!loading && !!sports?.length && (
                    <div className="tw-text-left tw-font-normal tw-space-y-4">
                        {sports.map((sport) => (
                            <div key={sport.id}>
                                <div className="tw-text-base">{sport.name}</div>
                                {sport.schedules?.length ? (
                                    sport.schedules?.map((session) => (
                                        <div key={session.id}>
                                            {session.title} {moment(session.start).format('HH:mm')} - {moment(session.end).format('HH:mm')}
                                        </div>
                                    ))
                                ) : (
                                    <div>{$t('No sessions for this sport')}</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

function VenuesDailyDemandMapper({ data, dates, level = 0, forceExpanded, expandKey }) {
    return data.map((dailyDemand) => {
        if (!dailyDemand.items.length) {
            return <VenuesDailyDemandTableRow key={dailyDemand.key} data={dailyDemand} level={level} dates={dates} />;
        }

        return (
            <VenuesDailyDemandTableGroupRow
                key={dailyDemand.key}
                data={dailyDemand}
                level={level}
                dates={dates}
                forceExpanded={forceExpanded}
                expandKey={expandKey}
            >
                {({ newLevel }) => (
                    <VenuesDailyDemandMapper data={dailyDemand.items} level={newLevel} dates={dates} forceExpanded={forceExpanded} expandKey={expandKey} />
                )}
            </VenuesDailyDemandTableGroupRow>
        );
    });
}

function VenuesDailyDemandTableGroupRow({ data, dates, children, level = 0, forceExpanded, expandKey }) {
    const [expanded, setExpanded] = useState(forceExpanded ?? false);

    const { selectedVenue } = useVenuesDailyDemand();

    // Sync with the toggle-all control. Re-run only when the toggle changes
    // (expandKey is bumped) so a user can still manually collapse one row
    // after the global toggle without it snapping back.
    useEffect(() => {
        setExpanded(!!forceExpanded);
    }, [expandKey]);

    return (
        <Fragment>
            <tr>
                <td className="tw-cursor-pointer tw-sticky tw-left-0 tw-h-fit tw-bg-white" onClick={() => setExpanded((state) => !state)}>
                    <div className="tw-flex tw-items-center tw-gap-x-2">
                        <div className="tw-font-semibold" style={{ paddingLeft: 6 * level }}>
                            {data.name}
                        </div>
                        {expanded ? <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" />}
                    </div>
                </td>
                <td className="tw-sticky tw-left-60 tw-h-fit inbud-numeric-cell tw-bg-white">{data.slug !== 'spectators' && formatFigure(data.number)}</td>
                {dates.map((date) => (
                    <VenuesDailyDemandTableCells key={`${selectedVenue.id}-${date}-${data.key}`} date={date} data={data} />
                ))}
            </tr>
            {expanded && children({ newLevel: level + 1 })}
        </Fragment>
    );
}

function VenuesDailyDemandTableRow({ data, dates, level = 0 }) {
    const { selectedVenue } = useVenuesDailyDemand();

    const isSpectators = data.slug === 'spectators';

    return (
        <tr className="!tw-bg-white">
            <td className="tw-sticky tw-left-0 tw-h-fit tw-bg-white">
                <div className={classNames({ 'tw-font-semibold': level === 0, 'tw-font-medium': level === 1 })} style={{ paddingLeft: 6 * level }}>
                    {data.name}
                </div>
            </td>
            <td className="tw-sticky tw-left-60 tw-h-fit inbud-numeric-cell tw-bg-white">{!isSpectators && formatFigure(data.number)}</td>
            {dates.map((date) => (
                <VenuesDailyDemandTableCells key={`${selectedVenue.id}-${date}-${data.key}`} date={date} data={data} editable={!isSpectators} />
            ))}
        </tr>
    );
}

const VenuesDailyDemandTableCells = ({ data, date, editable }) => {
    const { upsertDailyDemand } = useVenuesDailyDemand();

    const formattedDate = useMemo(() => moment(date, 'D-M-YYYY').format('YYYY-MM-DD'), [date]);
    const isSpectators = data.slug === 'spectators';

    const morning = useMemo(
        () => (Object.prototype.hasOwnProperty.call(data.days || {}, formattedDate) ? data.days[formattedDate].morning : null),
        [data, formattedDate],
    );
    const afternoon = useMemo(
        () => (Object.prototype.hasOwnProperty.call(data.days || {}, formattedDate) ? data.days[formattedDate].afternoon : null),
        [data, formattedDate],
    );
    const evening = useMemo(
        () => (Object.prototype.hasOwnProperty.call(data.days || {}, formattedDate) ? data.days[formattedDate].evening : null),
        [data, formattedDate],
    );

    const aggregated = useMemo(() => {
        if (!isSpectators) return null;
        const aggregatedDays = data.aggregatedDays || data.aggregated_days || {};
        return Object.prototype.hasOwnProperty.call(aggregatedDays, formattedDate) ? aggregatedDays[formattedDate] : 0;
    }, [data, formattedDate, isSpectators]);

    const handleUpsert = (period, value) => {
        const payload = {
            model_type: data.modelType,
            model_id: data.modelId,
            sport_id: data.sportId,
            date: formattedDate,
            period,
            value,
        };

        return upsertDailyDemand(payload);
    };

    if (isSpectators) {
        return (
            <td className="inbud-numeric-cell tw-border-l tw-snap-start tw-bg-white" colSpan={3}>
                {formatFigure(aggregated)}
            </td>
        );
    }

    return editable ? (
        <Fragment>
            <EditableVenuesDailyDemandTableCell value={morning} upsert={(value) => handleUpsert('morning', value)} className="tw-border-l tw-snap-start" />
            <EditableVenuesDailyDemandTableCell value={afternoon} upsert={(value) => handleUpsert('afternoon', value)} />
            <EditableVenuesDailyDemandTableCell value={evening} upsert={(value) => handleUpsert('evening', value)} />
        </Fragment>
    ) : (
        <Fragment>
            <td className="inbud-numeric-cell tw-border-l tw-snap-start">{formatFigure(morning, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(afternoon, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(evening, String)}</td>
        </Fragment>
    );
};

const EditableVenuesDailyDemandTableCell = ({ value, upsert, className }) => {
    const [editMode, setEditMode] = useState(false);

    const { updateDailyDemandByKey } = useVenuesDailyDemand();
    const toast = useToast();

    useEffect(() => {
        setEditMode(false);
    }, []);

    return (
        <Formik
            enableReinitialize
            initialValues={{
                value: value || '',
            }}
            validationSchema={Yup.object().shape({
                value: Yup.number().positive().optional(),
            })}
            onSubmit={(values, actions) => {
                if (values.value == value) {
                    actions.setSubmitting(false);
                    setEditMode(false);
                    return;
                }

                upsert(values.value || 0)
                    .then(({ data }) => {
                        setEditMode(false);

                        updateDailyDemandByKey(data.key, data);
                    })
                    .catch((e) => {
                        if (e?.response?.data?.error) {
                            return toast.error(e.response.data.error);
                        } else if (e?.response?.data?.message) {
                            return toast.error(e.response.data.message);
                        }

                        toast.error($t('Somethings went wrong!'));
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    });
            }}
        >
            {({ values, isValid, isSubmitting, resetForm, submitForm }) =>
                !editMode ? (
                    <td className={classNames('tw-cursor-pointer inbud-numeric-cell', className)} onClick={() => setEditMode((state) => !state)}>
                        {values.value}
                    </td>
                ) : (
                    <td className={className}>
                        <Form
                            onKeyDown={({ key }) => {
                                if (key === 'Escape') {
                                    resetForm();
                                    setEditMode(false);
                                } else if (key === 'Enter') {
                                    submitForm();
                                }
                            }}
                        >
                            <div className="tw-flex tw-items-center tw-gap-x-1">
                                <Field
                                    type="number"
                                    name="value"
                                    className={classNames(
                                        'input text-input !tw-h-auto !tw-text-xs !tw-py-0.5 !tw-px-2 !tw-m-0 !tw-border-none inbud-numeric-text',
                                        {
                                            invalid: !isValid,
                                        },
                                    )}
                                    min={0}
                                    disabled={isSubmitting}
                                    onBlur={submitForm}
                                    autoFocus
                                />
                                <button type="submit" className="tw-text-green-400 tw-rounded-full tw-bg-green-50/20">
                                    <CircleCheckIcon className="tw-w-5 tw-h-5" />
                                </button>
                            </div>
                        </Form>
                    </td>
                )
            }
        </Formik>
    );
};

const DateRangeControl = ({ start, end, minDate, maxDate, onChange, onPrev, onNext }) => {
    const [pendingStart, setPendingStart] = useState(null);

    const prevDisabled = useMemo(() => {
        if (!start || !minDate) return !start;
        return start.isSameOrBefore(minDate, 'day');
    }, [start, minDate]);

    const nextDisabled = useMemo(() => {
        if (!end || !maxDate) return !end;
        return end.isSameOrAfter(maxDate, 'day');
    }, [end, maxDate]);

    const displayStart = pendingStart || (start ? start.format('YYYY-MM-DD') : null);
    const displayEnd = pendingStart ? null : end ? end.format('YYYY-MM-DD') : null;

    const CustomInput = forwardRef(function CustomInput({ value, onClick, className }, ref) {
        return (
            <button className={className} onClick={onClick} ref={ref} type="button">
                {value || $t('Pick a date range')}
            </button>
        );
    });

    return (
        <div className="tw-flex tw-items-center tw-gap-x-2 tw-rounded-full tw-bg-blue tw-px-2 tw-py-1">
            <div className="tw-shrink-0">
                <IconButton variant="primary" onClick={onPrev} disabled={prevDisabled}>
                    <ChevronLeftIcon className="tw-w-3 tw-h-3" />
                </IconButton>
            </div>
            <div>
                <DateRangePickerField
                    wrapperClassName="!tw-flex tw-items-center"
                    startDate={displayStart}
                    endDate={displayEnd}
                    valueFormat="yyyy-MM-dd"
                    dateFormat="dd-MM-yyyy"
                    min={minDate ? minDate.format('YYYY-MM-DD') : null}
                    max={maxDate ? maxDate.format('YYYY-MM-DD') : null}
                    onChange={([nextStart, nextEnd]) => {
                        if (nextStart && !nextEnd) {
                            setPendingStart(nextStart);
                            return;
                        }
                        if (nextStart && nextEnd) {
                            setPendingStart(null);
                            onChange(nextStart, nextEnd);
                        }
                    }}
                    customInput={<CustomInput className="tw-text-sm tw-text-white tw-px-2" />}
                />
            </div>
            <div className="tw-shrink-0">
                <IconButton variant="primary" onClick={onNext} disabled={nextDisabled}>
                    <ChevronRightIcon className="tw-w-3 tw-h-3" />
                </IconButton>
            </div>
        </div>
    );
};
